import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { TEACHING_CARDS } from '../data/modules';
import { ChevronLeft, BookOpen } from 'lucide-react';

export default function Teaching() {
  const { lang, t } = useLanguage();
  const { id } = useParams();

  // Single teaching detail
  if (id && TEACHING_CARDS[id]) {
    const tc = TEACHING_CARDS[id];
    const title = lang === 'zh' ? tc.title_zh : tc.title_en;
    const useFor = lang === 'zh' ? tc.use_for_zh : tc.use_for_en;
    const content = lang === 'zh' ? tc.content_zh : tc.content_en;
    return (
      <div className="container-app py-8 sm:py-12 max-w-3xl animate-fade-in-up" data-testid="teaching-detail">
        <Link to="/teaching" className="inline-flex items-center text-sm text-ink-secondary hover:text-terracotta mb-6">
          <ChevronLeft size={16} /> {t('teaching_back')}
        </Link>
        {tc.video_id ? (
          <div className="rounded-3xl overflow-hidden mb-6 aspect-video bg-black" data-testid="teaching-video">
            <iframe
              src={`https://www.youtube.com/embed/${tc.video_id}`}
              title={title}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : tc.image_url ? (
          <div
            className="rounded-3xl overflow-hidden h-48 sm:h-64 bg-cover bg-center mb-6"
            style={{ backgroundImage: `url(${tc.image_url})` }}
          />
        ) : null}
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-ink mb-4">{title}</h1>
        <div className="flex flex-wrap gap-2 mb-8">
          <span className="text-xs text-ink-tertiary uppercase tracking-wider mr-1">{t('teaching_use_for')}:</span>
          {useFor.map((u) => (
            <span key={u} className="text-xs px-2 py-1 rounded-full bg-sage-light text-sage font-medium">
              {u}
            </span>
          ))}
        </div>
        <div className="card-base p-6 sm:p-8">
          <ol className="space-y-4">
            {content.map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="w-8 h-8 rounded-full bg-terracotta text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">
                  {i + 1}
                </span>
                <p className="text-ink-secondary leading-relaxed pt-1">{step}</p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    );
  }

  // Index of all teaching cards
  return (
    <div className="container-app py-8 sm:py-12 animate-fade-in-up" data-testid="teaching-index">
      <div className="flex items-center gap-3 mb-2">
        <BookOpen size={22} className="text-terracotta" />
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-terracotta">
          Free
        </span>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-ink mb-8">
        {t('teaching_title')}
      </h1>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {Object.entries(TEACHING_CARDS).map(([key, tc]) => (
          <Link
            key={key}
            to={`/teaching/${key}`}
            data-testid={`teach-card-${key}`}
            className="card-interactive overflow-hidden group"
          >
            <div
              className="h-40 bg-cover bg-center"
              style={{ backgroundImage: `url(${tc.image_url})` }}
            />
            <div className="p-5">
              <h3 className="font-heading text-lg font-semibold text-ink mb-2 group-hover:text-terracotta transition-colors">
                {lang === 'zh' ? tc.title_zh : tc.title_en}
              </h3>
              <p className="text-sm text-ink-secondary leading-relaxed line-clamp-2">
                {(lang === 'zh' ? tc.use_for_zh : tc.use_for_en).join(' · ')}
              </p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
